import React, { useEffect, useRef, useState } from 'react';

// 로컬 검증 때만 REACT_APP_PAPER_API_URL 로 바꾼다. 비워 두면 운영 주소.
const PAPER_API_URL = process.env.REACT_APP_PAPER_API_URL
  || 'https://vk727hnuda.execute-api.ap-northeast-2.amazonaws.com/paper/genai-arxiv-papaer';
const REQUEST_TIMEOUT_MS = 40000;

const TOPICS = [
  { id: 'AI', label: 'AI' },
  { id: 'ROBOT', label: 'ROBOT' },
  { id: 'QUANTUM', label: 'QUANTUM' },
  { id: 'METAVERSE', label: 'METAVERSE' },
];

const MODES = [
  { id: 'latest', label: '최신 논문', hint: '최근 30일 내 논문 가운데 주목받는 논문' },
  { id: 'classic', label: '고전 논문', hint: '3~10년 전 논문 가운데 인용이 많은 논문' },
];

const formatDate = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
};

const authorLine = (authors = []) => {
  if (authors.length <= 3) return authors.join(', ');
  return `${authors.slice(0, 3).join(', ')} 외 ${authors.length - 3}명`;
};

const PaperCard = ({ paper }) => (
  <article className="paper-card">
    <div className="paper-rank" aria-hidden="true">{String(paper.rank).padStart(2, '0')}</div>
    <div className="paper-body">
      <h3 className="paper-title">
        <a href={paper.link} target="_blank" rel="noopener noreferrer">{paper.title}</a>
      </h3>
      {paper.headline && <p className="paper-headline">{paper.headline}</p>}

      <ul className="paper-meta">
        <li>{formatDate(paper.published)}</li>
        {paper.citations != null && <li>인용 {paper.citations.toLocaleString('ko-KR')}회</li>}
        {paper.fresh && <li className="paper-badge">이번 주 신작</li>}
        {paper.venue && !/arxiv/i.test(paper.venue) && <li>{paper.venue}</li>}
      </ul>
      {paper.authors?.length > 0 && <p className="paper-authors">{authorLine(paper.authors)}</p>}

      {paper.summary_ko && <p className="paper-summary">{paper.summary_ko}</p>}
      {paper.reason && (
        <div className="paper-reason">
          <span className="paper-reason-label">추천 이유</span>
          <p>{paper.reason}</p>
        </div>
      )}

      {paper.abstract && (
        <details className="paper-details">
          <summary>초록 원문</summary>
          <p lang="en">{paper.abstract}</p>
        </details>
      )}
      {paper.basis && (
        <details className="paper-details">
          <summary>선정 근거</summary>
          <p>{paper.basis}</p>
        </details>
      )}

      <div className="paper-links">
        <a href={paper.link} target="_blank" rel="noopener noreferrer">arXiv에서 보기</a>
        <a href={paper.pdf_link} target="_blank" rel="noopener noreferrer">PDF</a>
      </div>
    </div>
  </article>
);

const SkeletonCards = () => (
  <div className="paper-list" aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <div key={i} className="paper-card paper-skeleton">
        <div className="paper-rank">{String(i + 1).padStart(2, '0')}</div>
        <div className="paper-body">
          <span className="skeleton-line wide" />
          <span className="skeleton-line" />
          <span className="skeleton-line short" />
        </div>
      </div>
    ))}
  </div>
);

const PaperSection = () => {
  const [topic, setTopic] = useState('');
  const [mode, setMode] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const controllerRef = useRef(null);

  useEffect(() => () => controllerRef.current?.abort(), []);

  const load = async (nextTopic, nextMode) => {
    if (!nextTopic || !nextMode) return; // 분야와 유형을 둘 다 골라야 요청한다
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const timer = setTimeout(() => controller.abort('timeout'), REQUEST_TIMEOUT_MS);

    setStatus('loading');
    setError('');
    try {
      const response = await fetch(PAPER_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ topic: nextTopic, mode: nextMode }),
        signal: controller.signal,
      });
      let body = null;
      try {
        body = await response.json();
      } catch {
        body = null;
      }
      if (!response.ok || !body) {
        throw new Error(body?.message || `서버가 응답하지 않습니다 (HTTP ${response.status}).`);
      }
      if (controllerRef.current !== controller) return;
      setData(body);
      setStatus('done');
    } catch (e) {
      if (controllerRef.current !== controller) return; // 더 새로운 요청이 이미 나갔다
      setStatus('error');
      setError(controller.signal.reason === 'timeout'
        ? '응답이 너무 오래 걸립니다. 잠시 뒤 다시 시도해 주세요.'
        : (e.message || '논문을 불러오지 못했습니다.'));
    } finally {
      clearTimeout(timer);
    }
  };

  const pickTopic = (id) => {
    setTopic(id);
    load(id, mode);
  };

  const pickMode = (id) => {
    setMode(id);
    load(topic, id);
  };

  const modeHint = MODES.find((m) => m.id === mode)?.hint;
  const papers = data?.papers || [];

  return (
    <section className="service-section">
      <h2>논문 추천 AI 서비스</h2>

      <fieldset className="field-group">
        <legend>분야</legend>
        <div className="button-container one-line">
          {TOPICS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`category-btn ${topic === t.id ? 'selected' : ''}`}
              aria-pressed={topic === t.id}
              onClick={() => pickTopic(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="field-group">
        <legend>유형</legend>
        <div className="button-container one-line">
          {MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              className={`category-btn ${mode === m.id ? 'selected' : ''}`}
              aria-pressed={mode === m.id}
              onClick={() => pickMode(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>
        {modeHint && <p className="paper-mode-hint">{modeHint}</p>}
      </fieldset>

      <div className="results paper-results" aria-live="polite" aria-busy={status === 'loading'}>
        {status === 'idle' && <p className="paper-placeholder">분야와 유형을 고르면 바로 추천을 시작합니다.</p>}

        {status === 'loading' && (
          <>
            <p className="paper-placeholder">논문을 찾고 있습니다. 그날 첫 요청은 20초 안팎 걸립니다.</p>
            <SkeletonCards />
          </>
        )}

        {status === 'error' && (
          <div className="paper-error">
            <p>{error}</p>
            <button type="button" className="category-btn" onClick={() => load(topic, mode)}>다시 시도</button>
          </div>
        )}

        {status === 'done' && data && (
          <>
            {/* <header> 는 쓰지 않는다. 전역 header 규칙(사이트 상단 스타일)이 그대로 걸린다. */}
            <div className="paper-header">
              <h3>{data.topic_label || data.topic} {data.mode === 'latest' ? '최신' : '고전'} 논문</h3>
              <ul className="paper-chips">
                {data.window?.label && <li>{data.window.label}</li>}
                {data.ranking?.pool != null && <li>후보 {data.ranking.pool}편에서 {papers.length}편</li>}
                {data.date_kst && <li>{formatDate(data.date_kst)} 기준</li>}
              </ul>
              {data.overview && <p className="paper-overview">{data.overview}</p>}
              {!data.explained && (
                <p className="paper-notice">AI 해설을 만들지 못해 논문 정보만 보여 드립니다.</p>
              )}
            </div>

            <div className="paper-list">
              {papers.map((p) => <PaperCard key={p.arxiv_id} paper={p} />)}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default PaperSection;
